import { authFetch, clearAccessToken, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useState, useEffect, useCallback } from 'react'
import cookie from 'react-cookies'
import NavBar from '../layout/NavBar'
import TopBar from '../layout/TopBar'
import Pagination from '../layout/Pagination'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faMagnifyingGlass,
  faCalendar,
  faCircleCheck
} from '@fortawesome/free-solid-svg-icons'
import ReactFilter from '../components/inputs/ReactFilterList'
import Toolbar from 'react-multi-date-picker/plugins/toolbar'
import DatePicker from 'react-multi-date-picker'
import { Table } from 'react-bootstrap'
import dayjs from '../utils/dayjsConfig'
import usePagination from '../layout/usePagination'
import { formatMoney } from '../helpers/utils'
import { buildDeliveryAssignmentGuidePdf } from '../settlements/helpers/pdfBuilder'
import CreateActionButton from '../components/action_button/CreateActionButton'
import {
  STATUS_OPTIONS,
  DATE_PICKER_SETTINGS,
  ASSIGN_STATUS_TYPES_DIC
} from './constants/constants'
import { MetroSpinner } from 'react-spinners-kit'
import AssignDocumentsModal from './components/AssignDocumentsModal'
import DocumentsInAssignDeliveryList from './components/DocumentsInAssignDeliveryList'
import LoadingScreen from '../components/LoadingScreen'

const ASSIGNMENT_STATUS_POLL_INTERVAL_MS = 3000
const ASSIGNMENT_STATUS_TIMEOUT_MS = 60000
const ASSIGNMENT_STATUS_MAX_ATTEMPTS = Math.ceil(
  ASSIGNMENT_STATUS_TIMEOUT_MS / ASSIGNMENT_STATUS_POLL_INTERVAL_MS
)

const AssignDeliveryView = () => {
  const [data, setData] = useState(null)
  const [allDataUids, setAllDataUids] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [documentIndicators, setDocumentIndicators] = useState(null)
  const title = 'Asignar entregas'
  const [filterData, setFilterData] = useState([])
  const [status, setStatus] = useState(STATUS_OPTIONS)
  const [deliverymen, setDeliverymen] = useState([])

  const [filters, setFilters] = useState([])
  const [fetchDataStatus, setFetchDataStatus] = useState({ filters: false })
  const [allSelectedFilterOptions, setAllSelectedFilterOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const [creationDateQuery, setCreationDateQuery] = useState(null)
  const [deliveryDateQuery, setDeliveryDateQuery] = useState(null)
  const [selectedCreationDates, setSelectedCreationDates] = useState(null)
  const [selectedDeliveryDates, setSelectedDeliveryDates] = useState(null)
  const { pagination, setPagination, handlePaginationClick } = usePagination({ per_page: 100 })
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [filterVersion, setFilterVersion] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [triggerSearch, setTriggerSearch] = useState(false)
  const [filterSelectionMade, setFilterSelectionMade] = useState(false)
  const [resetTriggeredByDeselection, setResetTriggeredByDeselection] = useState(false)

  const [isAllSelected, setIsAllSelected] = useState(false)

  const [selectedDocuments, setSelectedDocuments] = useState([])
  const [selectedDeliveryman, setSelectedDeliveryman] = useState(null)
  const [selectedDeliverymanLabel, setSelectedDeliverymanLabel] = useState(null)
  const [selectedVisitDate, setSelectedVisitDate] = useState(null)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failedCreation, setFailedCreation] = useState(false)
  const [successCreation, setSuccessCreation] = useState(false)
  const [pendingAssignmentBatch, setPendingAssignmentBatch] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)

  // AbortController
  const abortControllerRef = React.useRef(new AbortController())
  const pendingAssignmentBatchRef = React.useRef(null)
  const isPollingAssignmentBatchRef = React.useRef(false)
  const successAlertTimeoutRef = React.useRef(null)
  const failureAlertTimeoutRef = React.useRef(null)

  useEffect(() => {
    const handleBeforeUnload = () => {
      abortControllerRef.current.abort('Fetch Aborted by User.')
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      abortControllerRef.current.abort('Component unmounted')
      if (successAlertTimeoutRef.current) clearTimeout(successAlertTimeoutRef.current)
      if (failureAlertTimeoutRef.current) clearTimeout(failureAlertTimeoutRef.current)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [])

  useEffect(() => {
    pendingAssignmentBatchRef.current = pendingAssignmentBatch
  }, [pendingAssignmentBatch])

  const handleCloseFailedCreation = (e) => {
    e.preventDefault()
    setFailedCreation(false)
  }

  const handleCloseSuccessCreation = (e) => {
    e.preventDefault()
    setSuccessCreation(false)
  }

  const showFailureAlert = useCallback((message) => {
    if (successAlertTimeoutRef.current) clearTimeout(successAlertTimeoutRef.current)
    if (failureAlertTimeoutRef.current) clearTimeout(failureAlertTimeoutRef.current)
    setSuccessCreation(false)
    setFailedCreation(message)
    failureAlertTimeoutRef.current = setTimeout(() => setFailedCreation(false), 10000)
  }, [])

  const showSuccessAlert = useCallback((message) => {
    if (successAlertTimeoutRef.current) clearTimeout(successAlertTimeoutRef.current)
    if (failureAlertTimeoutRef.current) clearTimeout(failureAlertTimeoutRef.current)
    setFailedCreation(false)
    setSuccessCreation(message)
    successAlertTimeoutRef.current = setTimeout(() => setSuccessCreation(false), 10000)
  }, [])

  const handleUnauthorizedResponse = useCallback((response) => {
    if (response?.status !== 401) return false

    clearAccessToken()
    window.location.href = '/?deactivated=true'
    return true
  }, [])

  const parseResponseBody = async (response) => {
    try {
      return await response.json()
    } catch (error) {
      return null
    }
  }

  const getAssignType = useCallback((documents, deliverymanUid) => {
    if (deliverymanUid === 'remove_assignment') return 'unassign'

    const hasReassignDocuments = documents.some(
      (document) => document.status === ASSIGN_STATUS_TYPES_DIC.OPEN
    )

    return hasReassignDocuments ? 'reassign' : 'assign'
  }, [])

  const getExpectedFinalStatus = useCallback((assignType) => {
    return assignType === 'unassign'
      ? ASSIGN_STATUS_TYPES_DIC.TO_BE_ASSIGNED
      : ASSIGN_STATUS_TYPES_DIC.OPEN
  }, [])

  const buildSuccessMessage = useCallback((assignmentBatch) => {
    const totalDocuments = assignmentBatch.documentUids.length

    switch (assignmentBatch.assignType) {
      case 'assign':
      case 'reassign':
        return `${totalDocuments} documento(s) asignado(s) exitosamente a ${assignmentBatch.deliverymanLabel}`
      case 'unassign':
        return `Asignación removida: ${totalDocuments} documento(s) fueron desvinculado(s) exitosamente.`
      default:
        return false
    }
  }, [])

  const buildFailureMessage = useCallback((assignmentBatch) => {
    const totalDocuments = assignmentBatch.documentUids.length

    switch (assignmentBatch.assignType) {
      case 'assign':
      case 'reassign':
        return `Error al asignar ${totalDocuments} documento(s) a ${assignmentBatch.deliverymanLabel}`
      case 'unassign':
        return `Error al remover la asignación: ${totalDocuments} documento(s) no pudieron ser desvinculado(s). Por favor, vuelva a intentarlo.`
      default:
        return 'Error al asignar. Por favor, vuelva a intentarlo.'
    }
  }, [])

  const resetAssignModalState = useCallback(() => {
    setShowAssignModal(false)
    setSelectedDeliveryman(null)
    setSelectedDeliverymanLabel(null)
    setSelectedVisitDate(null)
  }, [])

  const fetchDeliveryRequestsStatus = useCallback(
    async (documentUids) => {
      const queryParams = new URLSearchParams({
        document_uids: documentUids.join(',')
      }).toString()

      const response = await authFetch(
        `${BASE_BACKEND_URL}/delivery_requests/status?${queryParams}`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      if (handleUnauthorizedResponse(response)) {
        return { errorType: 'unauthorized' }
      }

      const responseData = await parseResponseBody(response)

      if (!response.ok) {
        return {
          errorType: 'request_failed',
          statusCode: response.status,
          responseData
        }
      }

      return responseData
    },
    [handleUnauthorizedResponse]
  )

  const updateAllSelectedOptions = (identifier, selectedLabels) => {
    setAllSelectedFilterOptions((prevOptions) => {
      const updatedOptions = new Map(prevOptions)
      const labels = Array.isArray(selectedLabels)
        ? selectedLabels
        : Array.from(selectedLabels || [])
      const selectedIds = labels.map((label) => labelToIdMapping[label])
      updatedOptions.set(identifier, selectedIds)
      return updatedOptions
    })
    buildFiltersQuery()
  }

  const filterMappings = {
    'NOMBRE DE CLIENTE': 'client_uid',
    'NOMBRE DE ENTREGADOR': 'salesman_uid',
    ESTADO: 'status'
  }

  const statusFilterDir = [
    { name: 'POR ASIGNAR', value: ASSIGN_STATUS_TYPES_DIC.TO_BE_ASSIGNED },
    { name: 'ASIGNADO', value: ASSIGN_STATUS_TYPES_DIC.OPEN }
  ]

  const buildFiltersQuery = () => {
    let queryParam = ''
    allSelectedFilterOptions.forEach((value, key) => {
      if (value.length !== 0) {
        const paramKey = filterMappings[key]
        if (paramKey) {
          let selectedValues = [...value]
          queryParam += `&${paramKey}=${selectedValues.join(',')}`
        }
      }
    })
    pagination.current_page = 1
    setFilterQuery(queryParam)
  }

  const prepareFilterData = () => {
    const localLabelToIdMapping = {}
    if (filterData.length <= 0) {
      return
    }
    const clientOptions = filterData?.clients?.map((client) => {
      const displayText = `${client.uid} - ${client.name}`
      localLabelToIdMapping[displayText] = client.uid
      return displayText
    })

    const deliverymenOptions = filterData?.deliverymen?.map((user) => {
      const displayText = `${user.uid} - ${user.name}`
      localLabelToIdMapping[displayText] = user.uid
      return displayText
    })

    const statusOptions = statusFilterDir.map((option) => {
      localLabelToIdMapping[option.name] = option.value
      return option.name
    })

    const newFilters = [
      { key: 'NOMBRE DE CLIENTE', value: clientOptions },
      { key: 'NOMBRE DE ENTREGADOR', value: deliverymenOptions }
    ]
    setFilters(newFilters)
    setLabelToIdMapping(localLabelToIdMapping)
    setFetchDataStatus({ filters: true })
  }

  const handleDateFilter = (datesObj, dateType) => {
    const CREATION = 'creation'
    const DELIVERY = 'delivery'
    let formatedDates = []
    let startDate = new Date()
    let endDate = new Date()

    if (!datesObj || datesObj.length === 0) {
      const otherFiltersSelected = Array.from(allSelectedFilterOptions.values()).some(
        (value) => value.length > 0
      )
      if (dateType === CREATION) {
        setCreationDateQuery(null)
        setSelectedCreationDates(null)
      }
      if (dateType === DELIVERY) {
        setDeliveryDateQuery(null)
        setSelectedDeliveryDates(null)
      }
      setFilterSelectionMade(otherFiltersSelected)
      setResetTriggeredByDeselection(!otherFiltersSelected)
    } else {
      datesObj.map((date) => {
        formatedDates.push(new Date(date.unix * 1000))
      })
      if (dateType === CREATION) {
        setSelectedCreationDates(datesObj)
      }
      if (dateType === DELIVERY) {
        setSelectedDeliveryDates(datesObj)
      }
      if (formatedDates.length > 1) {
        startDate = formatedDates[0] < formatedDates[1] ? formatedDates[0] : formatedDates[1]
        endDate = formatedDates[0] < formatedDates[1] ? formatedDates[1] : formatedDates[0]
      } else if (formatedDates.length === 1) {
        startDate = formatedDates[0]
        endDate = formatedDates[0]
      }

      const startDateISO = startDate.toISOString()
      const endDateISO = endDate.toISOString()
      if (dateType === CREATION) {
        setCreationDateQuery(`&creation_start=${startDateISO}&creation_end=${endDateISO}`)
      }
      if (dateType === DELIVERY) {
        setDeliveryDateQuery(`&delivery_start=${startDateISO}&delivery_end=${endDateISO}`)
      }
      setFilterSelectionMade(true)
      setResetTriggeredByDeselection(false)
    }
    pagination.current_page = 1
  }

  const resetReactFilters = () => {
    setAllSelectedFilterOptions(new Map())
    setFilterQuery('')
    setCreationDateQuery(null)
    setDeliveryDateQuery(null)
    setSelectedCreationDates(null)
    setSelectedDeliveryDates(null)
    setSearchInput('')
    setFilterVersion((prevVersion) => prevVersion + 1)
    setTriggerSearch((prev) => !prev)
  }

  useEffect(() => {
    if (pendingAssignmentBatch) return undefined

    const interval = setInterval(() => {
      const fetchAll = async () => {
        await fetchDataFromServer()
      }
      fetchAll()
    }, 15000)
    return () => clearInterval(interval)
  }, [
    pagination.current_page,
    filterQuery,
    creationDateQuery,
    deliveryDateQuery,
    triggerSearch,
    searchInput,
    pendingAssignmentBatch?.startedAt
  ])

  useEffect(() => {
    const fetchAll = async () => {
      await fetchDataFromServer()
      setIsAllSelected(false)
      setSelectedDocuments([])
    }
    fetchAll()
  }, [pagination.current_page, filterQuery, creationDateQuery, deliveryDateQuery, triggerSearch])

  useEffect(() => {
    async function fetchAll() {
      await fetchFilterInfoFromServer()
    }
    fetchAll()
  }, [])

  useEffect(() => {
    prepareFilterData()
  }, [filterData])

  useEffect(() => {
    if (allSelectedFilterOptions.size > 0) {
      buildFiltersQuery()
    }
  }, [allSelectedFilterOptions])

  useEffect(() => {
    if (isAllSelected && !areAllCheckboxesSelected) {
      setIsAllSelected(false)
    }
  }, [selectedDocuments])

  const areAllCheckboxesSelected =
    selectedDocuments.length === allDataUids.length &&
    selectedDocuments.every((val, index) => val === allDataUids[index])

  const isAnyFilterSelected = () => {
    const filtersSelected = Array.from(allSelectedFilterOptions.entries()).some(
      ([key, value]) => value.length > 0
    )
    const creationDateSelected = creationDateQuery !== null && creationDateQuery.trim() !== ''

    const deliveryDateSelected = deliveryDateQuery !== null && deliveryDateQuery.trim() !== ''

    return filtersSelected || creationDateSelected || deliveryDateSelected
  }

  const fetchFilterInfoFromServer = async (loadingState = true) => {
    if (loadingState) {
      setFetchDataStatus((prev) => ({ ...prev, filters: true }))
    }
    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/delivery_requests?assign_index_filter_info=true`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )
      if (handleUnauthorizedResponse(response)) {
        console.error('Authorization error', response)
        return
      }

      const data = await response.json()
      if (data) {
        const users_for_delivery = data['deliverymen'].map((d) => ({
          value: d.uid,
          label: `${d.uid} - ${d.name}`
        }))
        setFilterData(data)
        setDeliverymen(users_for_delivery)
      }
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    } finally {
      setFetchDataStatus((prev) => ({ ...prev, filters: false }))
    }
  }

  const fetchDataFromServer = useCallback(async () => {
    setIsLoading(true)

    try {
      const per_page = pagination.per_page
      const page = pagination.current_page
      const hasFilters = filterQuery || creationDateQuery || deliveryDateQuery || searchInput
      const queryParams = [
        'assign_index_table=true',
        `page=${page}`,
        `per_page=${per_page}`,
        hasFilters ? 'filter_search=true' : '',
        filterQuery ? filterQuery.replace(/^&/, '') : '',
        creationDateQuery ? creationDateQuery.replace(/^&/, '') : '',
        deliveryDateQuery ? deliveryDateQuery.replace(/^&/, '') : '',
        searchInput ? `q=${encodeURIComponent(searchInput)}` : ''
      ]
        .filter(Boolean)
        .join('&')

      const response = await fetchWithAuth(
        `${BASE_BACKEND_URL}/delivery_requests?${queryParams}`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        }
      )

      if (!response) return

      setPagination((prev) => ({
        ...prev,
        total_pages: Math.ceil(response['count'] / prev.per_page)
      }))
      setData(response['table'])
      setDocumentIndicators(response['document_indicators'])
      setAllDataUids(response['uids_in_table'])
    } catch (error) {
      console.error('Error fetching delivery requests:', error)
    } finally {
      setIsLoading(false)
    }
  }, [pagination.current_page, filterQuery, creationDateQuery, searchInput, deliveryDateQuery])

  const pollPendingAssignmentBatch = async () => {
    const currentBatch = pendingAssignmentBatchRef.current
    if (!currentBatch) return

    const timedOut =
      currentBatch.attempts >= ASSIGNMENT_STATUS_MAX_ATTEMPTS ||
      dayjs().diff(dayjs(currentBatch.startedAt), 'millisecond') >= ASSIGNMENT_STATUS_TIMEOUT_MS

    if (timedOut) {
      showFailureAlert(buildFailureMessage(currentBatch))
      setPendingAssignmentBatch(null)
      await fetchDataFromServer()
      return
    }

    try {
      const deliveryRequestsStatus = await fetchDeliveryRequestsStatus(currentBatch.documentUids)

      if (
        deliveryRequestsStatus?.errorType === 'request_failed' ||
        deliveryRequestsStatus?.errorType === 'unauthorized'
      ) {
        setPendingAssignmentBatch((prevBatch) =>
          prevBatch ? { ...prevBatch, attempts: prevBatch.attempts + 1 } : prevBatch
        )
        return
      }

      const deliveryRequests = Array.isArray(deliveryRequestsStatus?.result)
        ? deliveryRequestsStatus.result
        : []

      const missingDocumentUids = Array.isArray(deliveryRequestsStatus?.missing_uids)
        ? deliveryRequestsStatus.missing_uids
        : []

      if (
        missingDocumentUids.length > 0 ||
        deliveryRequests.length !== currentBatch.documentUids.length
      ) {
        showFailureAlert(buildFailureMessage(currentBatch))
        setPendingAssignmentBatch(null)
        await fetchDataFromServer()
        return
      }

      const hasUnexpectedFinalStatus = deliveryRequests.some(
        (deliveryRequest) =>
          deliveryRequest.status !== currentBatch.expectedFinalStatus &&
          deliveryRequest.status !== ASSIGN_STATUS_TYPES_DIC.PROCESSING
      )

      if (hasUnexpectedFinalStatus) {
        showFailureAlert(buildFailureMessage(currentBatch))
        setPendingAssignmentBatch(null)
        await fetchDataFromServer()
        return
      }

      const hasProcessingDocuments = deliveryRequests.some(
        (deliveryRequest) => deliveryRequest.status === ASSIGN_STATUS_TYPES_DIC.PROCESSING
      )

      if (hasProcessingDocuments) {
        setPendingAssignmentBatch((prevBatch) =>
          prevBatch ? { ...prevBatch, attempts: prevBatch.attempts + 1 } : prevBatch
        )
        return
      }

      showSuccessAlert(buildSuccessMessage(currentBatch))
      setPendingAssignmentBatch(null)
      await fetchDataFromServer()
    } catch (error) {
      if (error?.name === 'AbortError') return

      console.error('Error polling assignment batch:', error)

      setPendingAssignmentBatch((prevBatch) =>
        prevBatch ? { ...prevBatch, attempts: prevBatch.attempts + 1 } : prevBatch
      )
    }
  }

  useEffect(() => {
    if (!pendingAssignmentBatch) return undefined

    const pollBatchStatuses = async () => {
      if (isPollingAssignmentBatchRef.current) return

      isPollingAssignmentBatchRef.current = true
      try {
        await pollPendingAssignmentBatch()
      } finally {
        isPollingAssignmentBatchRef.current = false
      }
    }

    pollBatchStatuses()
    const interval = setInterval(pollBatchStatuses, ASSIGNMENT_STATUS_POLL_INTERVAL_MS)

    return () => {
      clearInterval(interval)
      isPollingAssignmentBatchRef.current = false
    }
  }, [pendingAssignmentBatch?.startedAt])

  const handleSearch = (e) => {
    //Cosa de search
    setSearchInput(e.target.value)
    pagination.current_page = 1
  }

  const handleEnterSearch = (e) => {
    //Cosa de search
    if (e.key === 'Enter') {
      pagination.current_page = 1
      fetchDataFromServer()
    }
  }

  const closeAssignDocumentsModal = async (confirmed) => {
    if (!confirmed) {
      resetAssignModalState()
      return
    }

    if (areAllModalInputsFull()) {
      const selectedDocumentsSnapshot = [...selectedDocuments]
      const documentUids = selectedDocumentsSnapshot.map((document) => document.uid)
      const assignType = getAssignType(selectedDocumentsSnapshot, selectedDeliveryman)
      const assignmentBatch = {
        documentUids,
        assignType,
        deliverymanLabel: selectedDeliverymanLabel,
        expectedFinalStatus: getExpectedFinalStatus(assignType)
      }
      const jsonData = {
        business_uid: cookie.load('business_tenant_uid'),
        deliveryman: selectedDeliveryman,
        visit_date: selectedVisitDate,
        document_uids: documentUids
      }

      setSaving(true)
      const requestWasSuccessful = await sendAssignmentsToBackend(jsonData, assignmentBatch)
      setSaving(false)

      if (requestWasSuccessful) {
        setSelectedDocuments([])
        setIsAllSelected(false)
      }
    }

    resetAssignModalState()
  }

  const sendAssignmentsToBackend = async (body, assignmentBatch) => {
    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/delivery_requests`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body)
        }
      )

      if (handleUnauthorizedResponse(response)) {
        return false
      }

      const responseData = await parseResponseBody(response)

      if (!response.ok) {
        showFailureAlert(responseData?.error || 'Error al asignar. Por favor, vuelva a intentarlo.')
        return false
      }

      setPendingAssignmentBatch({
        ...assignmentBatch,
        startedAt: dayjs().toISOString(),
        attempts: 0
      })
      await fetchDataFromServer()
      return true
    } catch (error) {
      if (error?.name === 'AbortError') return false

      console.error('Error sending assignments to backend:', error)
      showFailureAlert('Error al asignar. Por favor, vuelva a intentarlo.')
      return false
    }
  }

  const areAllModalInputsFull = () => {
    return (
      (Boolean(selectedDeliveryman) && Boolean(selectedVisitDate)) ||
      selectedDeliveryman === 'remove_assignment'
    )
  }

  const handleSelectAllDocumentsInPage = () => {
    if (isAllSelected) {
      setIsAllSelected(false)
      setSelectedDocuments([])
    } else {
      setIsAllSelected(true)
      setSelectedDocuments(allDataUids)
    }
  }

  const addToSelectedDocuments = (documentUid, documentStatus) => {
    setSelectedDocuments([...selectedDocuments, { uid: documentUid, status: documentStatus }])
  }

  const deleteFromSelectedDocuments = (documentUid, documentStatus) => {
    setSelectedDocuments(selectedDocuments.filter((d) => d.uid !== documentUid))
  }

  const openAssignModal = () => {
    setShowAssignModal(true)
  }

  const selectedFilterLabels = () => {
    const labels = []
    allSelectedFilterOptions.forEach((selectedIds, filterName) => {
      const values = Array.from(selectedIds || [])
        .map((id) => Object.keys(labelToIdMapping).find((label) => labelToIdMapping[label] === id))
        .filter(Boolean)
      if (values.length > 0) labels.push(`${filterName}: ${values.join(', ')}`)
    })

    if (selectedCreationDates?.length)
      labels.push(`Fecha de creación: ${selectedCreationDates.join(' - ')}`)
    if (selectedDeliveryDates?.length)
      labels.push(`Fecha de entrega: ${selectedDeliveryDates.join(' - ')}`)
    return labels
  }

  const downloadFilteredDocuments = async () => {
    if (!data?.length) return

    setIsDownloading(true)
    try {
      const response = await fetchWithAuth(
        `${BACKEND_URL}/businesses/${cookie.load('business_id')}/delivery_requests?assign_index_table=true&assign_index_export=true${filterQuery || creationDateQuery || deliveryDateQuery || searchInput ? '&filter_search=true' : ''}${filterQuery || ''}${creationDateQuery || ''}${deliveryDateQuery || ''}${searchInput ? `&q=${encodeURIComponent(searchInput)}` : ''}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + getAccessToken()
          }
        }
      )

      buildDeliveryAssignmentGuidePdf({
        documents: response?.table || [],
        filters: selectedFilterLabels()
      })
    } catch (error) {
      console.error('Error downloading delivery assignment guide:', error)
      showFailureAlert('No se pudo descargar la guía de entregas. Inténtalo nuevamente.')
    } finally {
      setIsDownloading(false)
    }
  }

  const filtersArray = Array.isArray(filters) ? filters : Array.from(filters || [])
  const anyFilterEmpty = filtersArray.some((filter) => filter.value.length === 0)

  if (data === null) {
    return <LoadingScreen text="Cargando..." />
  }

  return (
    <div>
      <div>
        <div>
          <>
            {failedCreation && (
              <div
                className="alert alert-danger d-flex justify-content-between align-items-center"
                role="alert"
              >
                <div>
                  <svg
                    width="19"
                    height="16"
                    viewBox="0 0 19 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="warning-route-plan-icon"
                  >
                    <path
                      d="M9.16667 0.166992L0 16.0003H18.3333L9.16667 0.166992Z"
                      fill="#FEC047"
                    />
                    <path
                      d="M9.16563 3.49219L15.4406 14.3339H2.89062L9.16563 3.49219Z"
                      fill="#FEC047"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M8.33398 6.83398H10.0007V10.1673H8.33398V6.83398ZM8.33398 11.834H10.0007V13.5007H8.33398V11.834Z"
                      fill="#202020"
                    />
                  </svg>
                  <strong>{failedCreation}</strong>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={(event) => handleCloseFailedCreation(event)}
                  aria-label="Close"
                ></button>
              </div>
            )}
            {successCreation && (
              <div
                className="alert alert-success d-flex justify-content-between align-items-center"
                role="alert"
              >
                <div>
                  <FontAwesomeIcon className="color-circle-check" icon={faCircleCheck} />
                  <strong>{successCreation}</strong>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={(event) => handleCloseSuccessCreation(event)}
                  aria-label="Close"
                ></button>
              </div>
            )}
            <div className="index-header detailed-container">
              <h2 className="mb-20">{title}</h2>
            </div>
            <div className="index-table">
              <div className="show-area">
                <div className="assign-document-div">
                  <div className="assign-document-indicator-background">
                    <div
                      className="assign-document-indicator-subtitles"
                      title="Muestra la cantidad de documentos que aún no han sido asignados a ningún entregador."
                    >
                      <span className="material-symbols-outlined assign-document-pending-icon">
                        pending_actions
                      </span>
                      <span className="assign-document-indicator-text">
                        {`Documentos sin asignar:  ${documentIndicators != null ? documentIndicators.nonassigned_documents : '-'}`}
                      </span>
                    </div>
                    <div
                      className="assign-document-indicator-subtitles"
                      title="Muestra el total de documentos que ya fueron asignados pero aún no enviados a la app."
                    >
                      <span className="material-symbols-outlined assign-document-checked-icon">
                        check_circle
                      </span>
                      <span className="assign-document-indicator-text">
                        {`Documentos asignados:  ${documentIndicators != null ? documentIndicators.current_assigned_documents : '-'}`}
                      </span>
                    </div>
                    <div
                      className="assign-document-indicator-subtitles"
                      title="Muestra el número de entregadores activos disponibles para recibir entregas."
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="18"
                        viewBox="0 0 24 18"
                        fill="none"
                      >
                        <path
                          d="M20.2091 4.5H17.0182V0H2.12727C0.957273 0 0 1.0125 0 2.25V14.625H2.12727C2.12727 16.4925 3.55255 18 5.31818 18C7.08382 18 8.50909 16.4925 8.50909 14.625H14.8909C14.8909 16.4925 16.3162 18 18.0818 18C19.8475 18 21.2727 16.4925 21.2727 14.625H23.4V9L20.2091 4.5ZM5.31818 16.3125C4.43536 16.3125 3.72273 15.5588 3.72273 14.625C3.72273 13.6912 4.43536 12.9375 5.31818 12.9375C6.201 12.9375 6.91364 13.6912 6.91364 14.625C6.91364 15.5588 6.201 16.3125 5.31818 16.3125ZM19.6773 6.1875L21.762 9H17.0182V6.1875H19.6773ZM18.0818 16.3125C17.199 16.3125 16.4864 15.5588 16.4864 14.625C16.4864 13.6912 17.199 12.9375 18.0818 12.9375C18.9646 12.9375 19.6773 13.6912 19.6773 14.625C19.6773 15.5588 18.9646 16.3125 18.0818 16.3125Z"
                          fill="#597FA9"
                        />
                      </svg>
                      <span className="assign-document-indicator-text">
                        {`Entregadores:  ${documentIndicators != null ? documentIndicators.total_deliverymen : '-'}`}
                      </span>
                    </div>
                  </div>
                  <div className="index-buttons">
                    <CreateActionButton
                      text={isDownloading ? 'Descargando...' : 'Descargar'}
                      icon="download"
                      iconSize={22}
                      className="assign-delivery-download-button"
                      title={
                        data?.length > 0
                          ? 'Se descargan todos los documentos que cumplen los filtros aplicados.'
                          : 'No hay documentos en pantalla para descargar.'
                      }
                      disabled={!data?.length || isDownloading}
                      onClick={downloadFilteredDocuments}
                    />
                    <CreateActionButton
                      text="Asignar seleccionados"
                      icon="checklist"
                      iconSize={22}
                      className="assign-delivery-selected-button"
                      title={
                        selectedDocuments?.length > 0
                          ? ''
                          : 'Selecciona al menos un documento para activar esta opción.'
                      }
                      disabled={selectedDocuments?.length <= 0}
                      onClick={openAssignModal}
                    />
                  </div>
                </div>
                <div className="assign-modal-info-flex margint-5 mb-16">
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M24 12C24 18.6274 18.6274 24 12 24C5.37258 24 0 18.6274 0 12C0 5.37258 5.37258 0 12 0C18.6274 0 24 5.37258 24 12Z"
                      fill="#202020"
                    />
                    <path
                      d="M10.9577 17.6434C10.9577 17.2731 11.088 16.9577 11.3486 16.6971C11.6091 16.4366 11.9246 16.3063 12.2949 16.3063C12.6651 16.3063 12.9806 16.4366 13.2411 16.6971C13.5017 16.9577 13.632 17.2731 13.632 17.6434C13.632 18.0137 13.5017 18.3291 13.2411 18.5897C12.9806 18.8503 12.6651 18.9806 12.2949 18.9806C11.9246 18.9806 11.6091 18.8503 11.3486 18.5897C11.088 18.3291 10.9577 18.0137 10.9577 17.6434ZM13.4674 3.81943C13.4674 6.56229 13.2206 10.1829 12.7269 14.6811H11.8629C11.3829 9.90857 11.1429 6.288 11.1429 3.81943C11.9246 2.96914 12.6994 2.96914 13.4674 3.81943Z"
                      fill="white"
                    />
                  </svg>
                  <p className="b-text-price" style={{ margin: 0 }}>
                    <span className="b-text-bold">Nota: </span>
                    Selecciona uno o varios documentos, elige un entregador, define la fecha de
                    entrega y presiona “Asignar seleccionados” para completar la asignación.
                  </p>
                </div>
                {showAssignModal && (
                  <AssignDocumentsModal
                    selectedDocuments={selectedDocuments}
                    deliverymen={deliverymen}
                    selectedDeliveryman={selectedDeliveryman}
                    setSelectedDeliveryman={setSelectedDeliveryman}
                    setSelectedDeliverymanLabel={setSelectedDeliverymanLabel}
                    selectedVisitDate={selectedVisitDate}
                    setSelectedVisitDate={setSelectedVisitDate}
                    areAllModalInputsFull={areAllModalInputsFull}
                    showModal={true}
                    closeModal={closeAssignDocumentsModal}
                    saving={saving}
                  />
                )}
                <h4 className="text-sub-title">Filtrar documentos por:</h4>
                <div className="filters-search-container mb-10">
                  <FontAwesomeIcon className="filter-icon mr-10" icon={faFilter} />
                  <div className="filters-container">
                    <DatePicker
                      disabled={anyFilterEmpty}
                      range
                      rangeHover
                      dateSeparator=" - "
                      months={DATE_PICKER_SETTINGS.months}
                      weekDays={DATE_PICKER_SETTINGS.weekDays}
                      format="DD MMM"
                      weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
                      highlightToday={false}
                      numberOfMonths={2}
                      maxDate={dayjs().local().endOf('day').toDate()}
                      onChange={(dateObjects) => handleDateFilter(dateObjects, 'creation')}
                      value={selectedCreationDates}
                      plugins={[
                        <Toolbar
                          position="bottom"
                          names={{
                            deselect: 'Deseleccionar'
                          }}
                          sort={['deselect']}
                        />
                      ]}
                      render={(value, openCalendar) => {
                        return (
                          <div
                            key={`flr-date`}
                            className={`mr-10 ${anyFilterEmpty ? 'react-filter-disabled' : ''}`}
                          >
                            <div className="react-filter-box filter-input-width-datepicker">
                              <div
                                title="Fecha de creación"
                                onClick={!anyFilterEmpty ? openCalendar : undefined}
                                className="react-filter-box-clickable-zone"
                              >
                                <div className="react-filter-titles">
                                  {value?.length > 0 && (
                                    <>
                                      {Array.isArray(value) ? (
                                        <div className="filter-title-overflow">
                                          {value.join(' - ')}
                                        </div>
                                      ) : (
                                        <div className="filter-title-overflow">{value}</div>
                                      )}
                                      <FontAwesomeIcon
                                        className="color-calendar-selected"
                                        icon={faCalendar}
                                      />
                                    </>
                                  )}
                                  {value?.length == 0 && (
                                    <>
                                      <div className="filter-title-overflow">FECHA DE CREACIÓN</div>
                                      <FontAwesomeIcon
                                        className={`${anyFilterEmpty ? 'color-calendar-disabled' : 'color-calendar-active'}`}
                                        icon={faCalendar}
                                      />{' '}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }}
                    />
                    {filters.map((filter, indx) => (
                      <div key={`flr-${indx}`} className="mr-10">
                        <ReactFilter
                          className="mr-10"
                          title={filter.key}
                          identifier={filter.key}
                          options={filter.value}
                          resetWatcher={filterVersion}
                          onSelectionChange={updateAllSelectedOptions}
                          disabled={anyFilterEmpty}
                        />
                      </div>
                    ))}
                    <DatePicker
                      disabled={anyFilterEmpty}
                      range
                      rangeHover
                      dateSeparator=" - "
                      months={DATE_PICKER_SETTINGS.months}
                      weekDays={DATE_PICKER_SETTINGS.weekDays}
                      format="DD MMM"
                      weekStartDayIndex={DATE_PICKER_SETTINGS.weekStartDayIndex}
                      highlightToday={false}
                      numberOfMonths={2}
                      onChange={(dateObjects) => handleDateFilter(dateObjects, 'delivery')}
                      value={selectedDeliveryDates}
                      plugins={[
                        <Toolbar
                          position="bottom"
                          names={{
                            deselect: 'Deseleccionar'
                          }}
                          sort={['deselect']}
                        />
                      ]}
                      render={(value, openCalendar) => {
                        return (
                          <div
                            key={`flr-date`}
                            className={`mr-10 ${anyFilterEmpty ? 'react-filter-disabled' : ''}`}
                          >
                            <div className="react-filter-box filter-input-width-datepicker">
                              <div
                                title="Fecha de entrega"
                                onClick={!anyFilterEmpty ? openCalendar : undefined}
                                className="react-filter-box-clickable-zone"
                              >
                                <div className="react-filter-titles">
                                  {value?.length > 0 && (
                                    <>
                                      {Array.isArray(value) ? (
                                        <div className="filter-title-overflow">
                                          {value.join(' - ')}
                                        </div>
                                      ) : (
                                        <div className="filter-title-overflow">{value}</div>
                                      )}
                                      <FontAwesomeIcon
                                        className="color-calendar-selected"
                                        icon={faCalendar}
                                      />
                                    </>
                                  )}
                                  {value?.length == 0 && (
                                    <>
                                      <div className="filter-title-overflow">FECHA DE ENTREGA</div>
                                      <FontAwesomeIcon
                                        className={`${anyFilterEmpty ? 'color-calendar-disabled' : 'color-calendar-active'}`}
                                        icon={faCalendar}
                                      />{' '}
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      }}
                    />
                    <div key={`flr-status`} className="mr-10">
                      <ReactFilter
                        className="mr-10"
                        title={'ESTADO'}
                        identifier={'ESTADO'}
                        options={['POR ASIGNAR', 'ASIGNADO']}
                        resetWatcher={filterVersion}
                        onSelectionChange={updateAllSelectedOptions}
                        disabled={anyFilterEmpty}
                      />
                    </div>
                  </div>
                  <div className="search-input-containter">
                    <input
                      value={searchInput}
                      onChange={handleSearch}
                      onKeyDown={handleEnterSearch}
                      className="round-search-input"
                      style={{ fontSize: 12 }}
                      placeholder="Buscar por # de documento o nombre de cliente"
                    />
                    {searchInput === '' && (
                      <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
                    )}
                    {searchInput && (
                      <div
                        title="Limpiar texto"
                        onClick={resetReactFilters}
                        className="round-search-input-x"
                      >
                        X
                      </div>
                    )}
                  </div>
                </div>
                <div className="mb-20">
                  {isAnyFilterSelected() && (
                    <a onClick={resetReactFilters} className="reset-filter cursor-pointer mb-20">
                      Restablecer filtros aplicados
                    </a>
                  )}
                </div>
                <Table responsive>
                  <thead className="table-header-gray">
                    <tr>
                      <th className="vertical-align-middle" style={{ padding: '10px 25px' }}>
                        <input
                          id="selectAllCheckbox"
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={() => handleSelectAllDocumentsInPage()}
                          disabled={isLoading}
                        />
                      </th>
                      <th className="vertical-align-middle">FECHA DE CREACIÓN</th>
                      <th className="vertical-align-middle">CÓDIGO DE CLIENTE</th>
                      <th className="vertical-align-middle">NOMBRE DE CLIENTE</th>
                      <th className="vertical-align-middle">TIPO DE CLIENTE</th>
                      <th className="vertical-align-middle">DIRECCIÓN DEL CLIENTE</th>
                      <th className="vertical-align-middle"># DE DOCUMENTO</th>
                      <th className="vertical-align-middle">TIPO DE DOCUMENTO</th>
                      <th className="vertical-align-middle">TOTAL</th>
                      <th className="vertical-align-middle">ENTREGADOR ASIGNADO</th>
                      <th className="vertical-align-middle">FECHA DE ENTREGA</th>
                      <th className="vertical-align-middle">ESTADO</th>
                      <th className="vertical-align-middle">ACCIÓN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan="13" className="td-centered">
                          <div className="saving-route-box">
                            <MetroSpinner size={24} color="#747474" />
                            <span className="saving-route-box-title">Cargando documentos...</span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {!isLoading &&
                      data &&
                      data.length > 0 &&
                      data.map((model, index) => (
                        <DocumentsInAssignDeliveryList
                          document={model}
                          documentIndex={index}
                          selectedDocuments={selectedDocuments}
                          addToSelectedDocuments={addToSelectedDocuments}
                          deleteFromSelectedDocuments={deleteFromSelectedDocuments}
                          openAssignModal={openAssignModal}
                        />
                      ))}
                    {!isLoading && data && data.length == 0 && (
                      <tr>
                        <td colSpan="13" className="td-text-limited td-text-limited-space">
                          No hay documentos disponibles en estos momentos.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
            <nav className="index-table">
              {pagination.total_pages > 1 && (
                <div onClick={handlePaginationClick}>
                  <Pagination
                    current_page={pagination.current_page}
                    total_pages={pagination.total_pages}
                  />
                </div>
              )}
            </nav>
          </>
        </div>
      </div>
    </div>
  )
}
export default AssignDeliveryView
